import { z } from "zod";
/**
 * Contracts of the studio's own receivables (spec 015): the commercial current account of each
 * client company with the studio. Not the monotributo current account in ARCA. Amounts are pesos,
 * always positive, as decimal text in payloads; a reversal is a void, never a negative movement.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "La fecha tiene que ser AAAA-MM-DD.");
/** First day of a month, `AAAA-MM-01`. */
const monthStart = z
    .string()
    .regex(/^\d{4}-\d{2}-01$/, "El mes tiene que ser AAAA-MM-01 (primer día del mes).");
/**
 * The largest amount one movement may carry: cien mil millones de pesos. Far above any fee, and low
 * enough that every sum the studio's screens make stays exact in integer cents (Codex 015 M1).
 * The database enforces the same ceiling.
 */
export const MAX_RECEIVABLE_AMOUNT = "99999999999.99";
const positiveAmount = z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim())
    .refine((v) => /^\d+(\.\d{1,2})?$/.test(v) && /[1-9]/.test(v), {
    message: "El importe tiene que ser mayor que cero, con hasta dos decimales (ej. 1234.56).",
})
    .refine((v) => Number(v.split(".")[0]) <= 99_999_999_999, {
    message: "El importe no puede superar los cien mil millones de pesos.",
});
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === "" ? undefined : v));
export const PAYMENT_METHODS = ["efectivo", "transferencia", "cheque", "otro"];
export const PAYMENT_METHOD_LABELS = {
    efectivo: "Efectivo",
    transferencia: "Transferencia",
    cheque: "Cheque",
    otro: "Otro",
};
export const CHARGE_SOURCES = ["manual", "monthly_fee"];
/** The concept every automatic monthly charge carries. */
export const MONTHLY_FEE_CONCEPT = "Honorarios mensuales";
/** Concepts offered to a studio the first time it opens its list. */
export const DEFAULT_RECEIVABLE_CONCEPTS = [MONTHLY_FEE_CONCEPT, "Balance anual"];
export const chargeCreateSchema = z
    .object({
    conceptName: z.string().trim().min(1, "Elegí o escribí el concepto.").max(120),
    description: optionalText(300),
    chargeDate: isoDate,
    dueDate: isoDate.optional(),
    amount: positiveAmount,
})
    .refine((v) => v.dueDate === undefined || v.dueDate >= v.chargeDate, {
    message: "El vencimiento no puede ser anterior a la fecha del cargo.",
    path: ["dueDate"],
});
export const paymentCreateSchema = z.object({
    paymentDate: isoDate,
    amount: positiveAmount,
    method: z.enum(PAYMENT_METHODS, { message: "Elegí cómo pagó." }),
    note: optionalText(300),
});
export const voidSchema = z.object({
    reason: z.string().trim().min(1, "Contá por qué lo anulás.").max(500),
});
export const conceptCreateSchema = z.object({
    name: z.string().trim().min(1, "El concepto necesita un nombre.").max(120),
});
export const monthlyFeeSetSchema = z.object({
    amount: positiveAmount,
    chargeDay: z
        .number()
        .int()
        .min(1, "El día de cobro va de 1 a 31.")
        .max(31, "El día de cobro va de 1 a 31."),
    dueDays: z
        .number()
        .int()
        .min(0, "Los días para el vencimiento van de 0 a 90.")
        .max(90, "Los días para el vencimiento van de 0 a 90.")
        .nullable()
        .optional(),
    startMonth: monthStart,
});
export const accountQuerySchema = z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
});
// ─── Contacts of a client company ────────────────────────────────────────────────────────────────
/** Deliberately plain: a format check, not a deliverability promise (spec FR-002). */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const contactName = z.string().trim().min(1, "El contacto necesita un nombre o un rol.").max(120);
const contactEmails = z
    .array(z.string().trim().toLowerCase())
    .max(10)
    .superRefine((emails, ctx) => {
    for (const email of emails) {
        if (!EMAIL.test(email)) {
            ctx.addIssue({ code: "custom", message: `El mail «${email}» no es válido.` });
            return;
        }
    }
    if (new Set(emails).size !== emails.length) {
        ctx.addIssue({ code: "custom", message: "Hay un mail repetido en el contacto." });
    }
});
const contactPhones = z.array(z.string().trim().min(1).max(40)).max(10);
/** Empty text clears the note. */
const contactNote = z
    .string()
    .trim()
    .max(500)
    .nullable()
    .transform((v) => (v === "" ? null : v));
export const contactInputSchema = z.object({
    name: contactName,
    emails: contactEmails.default([]),
    phones: contactPhones.default([]),
    note: contactNote.optional(),
    isDefault: z.boolean().default(false),
});
/** A partial update: an absent field is left as it is (no defaults here, on purpose). */
export const contactUpdateSchema = z.object({
    name: contactName.optional(),
    emails: contactEmails.optional(),
    phones: contactPhones.optional(),
    note: contactNote.optional(),
    isDefault: z.boolean().optional(),
});
//# sourceMappingURL=receivables.js.map